import type { Gym } from '../types';

/**
 * Merge and re-rank gym name-search results from SEVERAL states into one list.
 *
 * ⚠️⚠️ THIS IS A DELIBERATE MIRROR OF SERVER LOGIC, AND THAT IS THE COST OF ITEM 3's
 * CHOSEN ROUTE — say so rather than let a reader discover it. The authoritative ranking
 * lives in `functions/src/searchHandlers.ts` (`searchGymsByNameFromDisplayCore`): exact
 * name match first, then names starting with the query, then alphabetical, capped at 10.
 * The two cannot share code — `functions/` is a separate package with its own deploy —
 * so this is a copy, and copies drift. **If the server's ordering changes, change this.**
 *
 * ⭐ WHY A MERGE IS NEEDED AT ALL, rather than concatenation. Each state is searched
 * separately and each returns its own top 10, already sorted. Concatenating gives NSW's
 * alphabetical also-rans ahead of VIC's exact match. Re-ranking across the union is the
 * only way "best match first" survives the merge — and it is the operator's ruling
 * (2026-08-17): best matches first, regardless of which state they are in.
 *
 * ⚠️ THE CAP IS APPLIED AFTER MERGING, NOT BEFORE. Ten per state would be twenty rows in
 * a dropdown built for ten.
 */

/** The server's cap, mirrored. See the note above before changing it. */
export const GYM_NAME_SEARCH_LIMIT = 10;

/**
 * The server's relevance comparator, mirrored.
 *
 * `searchTerm` must already be lower-cased and trimmed, exactly as the server compares it.
 */
export const compareGymNameRelevance = (a: Gym, b: Gym, searchTerm: string): number => {
    const aLower = (a.name ?? '').toLowerCase();
    const bLower = (b.name ?? '').toLowerCase();

    if (aLower === searchTerm && bLower !== searchTerm) return -1;
    if (bLower === searchTerm && aLower !== searchTerm) return 1;

    if (aLower.startsWith(searchTerm) && !bLower.startsWith(searchTerm)) return -1;
    if (bLower.startsWith(searchTerm) && !aLower.startsWith(searchTerm)) return 1;

    return aLower.localeCompare(bLower);
};

/**
 * Merge per-state result lists into one ranked, de-duplicated, capped list.
 *
 * ⚠️ DE-DUPLICATION IS BY GYM ID and is NOT theoretical hygiene: a gym near a border can
 * legitimately surface in a radius-flavoured query from either side, and a duplicated row
 * in a ten-row dropdown costs a real result its place.
 */
export const mergeGymNameResults = (
    perState: ReadonlyArray<readonly Gym[]>,
    rawQuery: string,
    limit: number = GYM_NAME_SEARCH_LIMIT,
): Gym[] => {
    const searchTerm = rawQuery.trim().toLowerCase();
    const byId = new Map<string, Gym>();

    for (const list of perState) {
        for (const gym of list) {
            if (!byId.has(gym.id)) byId.set(gym.id, gym);
        }
    }

    return [...byId.values()]
        .sort((a, b) => compareGymNameRelevance(a, b, searchTerm))
        .slice(0, limit);
};
